import React, { useState, useEffect } from "react";
import { Tabs, Card, Avatar, Typography, Badge } from "antd";
import { Bell, BellDot } from "lucide-react";
import {
  bp_get_batch_notification_for_employee,
  get_approval_notifications,
  mark_approval_notification_read,
} from '@/utils/api';
import {
  approvalNotificationInitials,
  buildApprovalNotificationPath,
} from '@/utils/approvalNotificationNav';
import { batchInboxInitials, formatOperationLabel } from '@/utils/batchNotificationDisplay';
import { useNavigate, useSearchParams } from "react-router-dom";
import { useSelector } from "react-redux";
import { defaultColors } from '@/themes/antd-theme';
import { formatDateTime } from '@/utils/datetime';

const { Paragraph } = Typography;

// ==============================|| NOTIFICATION PAGE ||============================== //

const NotificationPage = () => {
  const [notifications_data, set_notifications_data] = useState([]);
  const [alert_data, set_alert_data] = useState([]);
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  // recipe_type sent as string 'true' (Production) / 'false' (Test).
  const recipeTypeFlag = useSelector((state) => state.testProdView) === 'production';

  const success_get_notification = (res) => {
    if (res?.error) return;
    set_notifications_data(res.data.data);
  };

  const success_get_alerts = (res) => {
    if (res?.error) return;
    set_alert_data(res?.data?.data || []);
  };

  useEffect(() => {
    bp_get_batch_notification_for_employee(success_get_notification, { recipe_type: recipeTypeFlag });
    get_approval_notifications(success_get_alerts, { recipe_type: recipeTypeFlag });
  }, [recipeTypeFlag]);

  const initialTab = searchParams.get('tab') === 'approval' ? 'alert' : 'notify';
  const [activeKey, set_activeKey] = useState(initialTab);

  const handleApprovalClick = (item) => {
    const path = buildApprovalNotificationPath(item);
    const id = item?.notification_id;
    if (id && !item?._legacy && !String(id).startsWith('legacy-')) {
      mark_approval_notification_read(id, () => {
        set_alert_data((prev) =>
          (prev || []).map((row) =>
            row.notification_id === id ? { ...row, is_read: true } : row
          )
        );
      });
    }
    if (path) navigate(path);
  };

  const items = [
    {
      key: "notify",
      label: (
        <div className="flex items-center gap-2">
          <span>Notifications</span>
          <Bell
            size={20}
            style={{ color: defaultColors.success }}
          />
        </div>
      ),
      children: (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4 p-2">
          {notifications_data?.map((item, index) => (
            <Card key={index} className="hover:shadow-md transition-shadow">
              <div className="flex gap-3">
                <Avatar
                  size={36}
                  style={{
                    color: defaultColors.success,
                    backgroundColor: defaultColors.palette.green[50],
                  }}
                >
                  {batchInboxInitials(item)}
                </Avatar>
                <div className="flex-1">
                  <h6 className="text-base font-semibold m-0 mb-2">{item.msg}</h6>

                  <div className="grid grid-cols-2 gap-1 text-xs">
                    <span className="text-gray-600 dark:text-gray-400">Recipe:</span>
                    <span className="text-gray-900 dark:text-gray-100 truncate">
                      {item?.recipe_name}
                    </span>

                    <span className="text-gray-600 dark:text-gray-400">Batch Number:</span>
                    <span className="text-gray-900 dark:text-gray-100 truncate">
                      {item?.batch_number}
                    </span>

                    <span className="text-gray-600 dark:text-gray-400">Process Name:</span>
                    <span className="text-gray-900 dark:text-gray-100 truncate">
                      {item?.process_name}
                    </span>

                    {formatOperationLabel(item) ? (
                      <>
                        <span className="text-gray-600 dark:text-gray-400">Operation:</span>
                        <span className="text-gray-900 dark:text-gray-100 truncate">
                          {formatOperationLabel(item)}
                        </span>
                      </>
                    ) : null}

                    {item?.status ? (
                      <>
                        <span className="text-gray-600 dark:text-gray-400">Status:</span>
                        <span className="text-gray-900 dark:text-gray-100 truncate">
                          {item.status}
                        </span>
                      </>
                    ) : null}

                    <span className="text-gray-600 dark:text-gray-400">Schedule Time:</span>
                    <span className="text-gray-900 dark:text-gray-100 truncate">
                      {formatDateTime(item?.batch_schedule_time || item?.created_at || item?.requested_at)}
                    </span>
                  </div>
                </div>
              </div>
            </Card>
          ))}
        </div>
      ),
    },
    {
      key: "alert",
      label: (
        <div className="flex items-center gap-2">
          <span>Approval Notifications</span>
          <BellDot
            size={20}
            style={{ color: defaultColors.warning }}
          />
        </div>
      ),
      children: (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4 p-2">
          {alert_data?.map((item, index) => (
            <Card
              key={item?.notification_id ?? index}
              className="hover:shadow-md transition-shadow cursor-pointer"
              onClick={() => handleApprovalClick(item)}
            >
              <div className="flex gap-3">
                <Badge dot={item?.is_read !== true} offset={[-2, 2]}>
                  <Avatar
                    size={36}
                    style={{
                      color: defaultColors.warning,
                      backgroundColor: defaultColors.palette.amber[50],
                    }}
                  >
                    {approvalNotificationInitials(item)}
                  </Avatar>
                </Badge>
                <div className="flex-1">
                  {(item?.title || item?.module) && (
                    <div className="text-xs font-semibold mb-1" style={{ color: defaultColors.warning }}>
                      {item?.title || item?.module}
                    </div>
                  )}
                  <Paragraph
                    ellipsis={{
                      rows: 2,
                      expandable: false,
                    }}
                    className="text-sm font-medium mb-2"
                  >
                    {item?.message || item?.msg}
                  </Paragraph>

                  <div className="flex flex-col gap-1 text-xs">
                    <div className="text-gray-600 dark:text-gray-400">
                      Date: {formatDateTime(item?.created_at || item?.date)}
                    </div>
                    {item?.entity_name || item?.entity_code ? (
                      <div className="text-gray-600 dark:text-gray-400 truncate">
                        {item?.entity_code || item?.entity_name}
                      </div>
                    ) : null}
                  </div>
                </div>
              </div>
            </Card>
          ))}
        </div>
      ),
    },
  ];

  return (
    <Tabs
      activeKey={activeKey}
      onChange={(value) => {
        set_activeKey(value);
        if (value === "alert" && !alert_data?.length) {
          get_approval_notifications(success_get_alerts, { recipe_type: recipeTypeFlag });
        }
      }}
      items={items}
    />
  );
};

export default NotificationPage;
